import { noop, pipe } from '@kofno/piper';
import { Task } from 'taskarian';

import { errorMessage } from '../../ExceptionHandling';
import { DataLayer, GtmDataLayer, gtmLoadError, GtmLoadError, PageTitle } from './Types';

declare global {
  export interface Window {
    dataLayer?: DataLayer[];
  }
}

// Google Tag Manager reads window.dataLayer as a plain array. Besides our own
// DataLayer records it accepts its own bootstrap event, consent calls (an
// `arguments` object) and reset functions, so pushes go through this
// untyped view of the same array. Nothing here injects inline <script> text,
// which keeps the page compatible with a CSP that has no 'unsafe-inline'.
const rawDataLayer = (): unknown[] => {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer as unknown[];
};

// The standard GTM snippet, as a function instead of inline script text.
const loadGtmContainer = (token: string): void => {
  rawDataLayer().push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(token)}`;
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(script, first);
  } else {
    document.head.appendChild(script);
  }
};

// gtag() pushes its `arguments` object, not an array. GTM's consent handling
// depends on that shape.
function gtag(..._args: unknown[]): void {
  rawDataLayer().push(arguments);
}

const setDefaultConsent = (): void => {
  gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
  });
};

const isConsentDefault = (entry: unknown): boolean => {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const args = entry as { 0?: unknown; 1?: unknown };
  return args[0] === 'consent' && args[1] === 'default';
};

// Osano loads before the app and, with Google Consent Mode on, pushes its own
// consent default and the visitor's updates. A second default from us would
// conflict with those, so ours only applies when no default exists yet.
const hasConsentDefault = (): boolean => rawDataLayer().some(isConsentDefault);

// window.dataLayer can exist before the containers load, because Osano creates
// it for Google Consent Mode. This flag, not the array, records that the
// containers were already requested.
let containersRequested = false;

export const loadGoogleTagManager = (
  token: string,
  newToken: string,
): Task<GtmLoadError, GtmDataLayer> => {
  return new Task<GtmLoadError, GtmDataLayer>((reject, resolve) => {
    const rejectException = pipe(errorMessage, gtmLoadError, reject);
    try {
      if (containersRequested && window.dataLayer) {
        resolve({ dataLayer: window.dataLayer });
      } else {
        if (document.head) {
          if (!hasConsentDefault()) {
            setDefaultConsent();
          }
          loadGtmContainer(token);
          loadGtmContainer(newToken);
          containersRequested = true;
        }

        // No GTM <noscript> iframe: this app cannot run without JavaScript,
        // and an iframe built with the DOM API loads even inside <noscript>,
        // which the CSP frame-src then blocks and reports.
        if (document.body) {
          if (window.dataLayer) {
            resolve({ dataLayer: window.dataLayer });
          } else {
            reject(gtmLoadError('window.dataLayer not loaded by script'));
          }
        }
      }
    } catch (e) {
      rejectException(e);
    }

    return noop;
  });
};

export const dataLayerPageTitle = (pageTitle: PageTitle) => {
  rawDataLayer().push(pageTitle);
};

export const dataLayer = (dataLayer: DataLayer) => {
  const layer = rawDataLayer();
  layer.push(function (this: { reset: () => void }) {
    this.reset();
  });
  layer.push(dataLayer);
};
